import { useMemo, useState } from "react";
import { Minus, Plus, Search, Trash2 } from "lucide-react";
import { fmtExpiry } from "@/lib/money";

export function ProductPicker({ products, onAdd }) {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return products.filter((p) => !s || p.name.toLowerCase().includes(s) || (p.barcode || "").includes(s)).slice(0, 40);
  }, [products, q]);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <div className="relative border-b border-slate-100 p-3">
        <Search className="pointer-events-none absolute left-6 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or barcode" data-testid="tags-search-input" className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm outline-none focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-amber-200" />
      </div>
      <ul className="max-h-64 overflow-y-auto p-1.5" data-testid="tags-search-results">
        {shown.map((p) => (
          <li key={p.id}>
            <button onClick={() => onAdd(p)} data-testid={`tags-add-${p.id}`} className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-amber-50">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-slate-900">{p.name}</span>
                <span className="block font-mono text-[10.5px] text-slate-400">{p.barcode || "no barcode yet"}</span>
              </span>
              <Plus className="h-4 w-4 shrink-0 text-amber-600" />
            </button>
          </li>
        ))}
        {shown.length === 0 && <li className="px-3 py-6 text-center text-sm text-slate-400">No products match</li>}
      </ul>
    </div>
  );
}

function BatchSelect({ s, onChange }) {
  const opts = s.product.batches || [];
  if (!opts.length) return <div className="text-[11px] font-medium text-rose-600">No batches in this branch</div>;
  return (
    <select value={s.batchId || ""} onChange={(e) => onChange(e.target.value)} data-testid={`tags-batch-select-${s.product.id}`} className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2 font-mono text-[11px] outline-none focus:border-slate-900">
      {opts.map((b) => (
        <option key={b.id} value={b.id}>
          {b.batch_no} · exp {fmtExpiry(b.expiry)} · qty {b.qty}{b.expired ? " · EXPIRED" : ""}
        </option>
      ))}
    </select>
  );
}

export function SelectedList({ sel, pharmacy, setCopies, setBatch, remove }) {
  if (!sel.length) return <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500" data-testid="tags-selected-empty">No products selected yet</div>;
  return (
    <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="tags-selected-list">
      {sel.map((s) => (
        <li key={s.product.id} className="space-y-2 px-3 py-3" data-testid={`tags-selected-${s.product.id}`}>
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-slate-900">{s.product.name}</div>
              <div className={`font-mono text-[10.5px] ${s.product.barcode ? "text-slate-400" : "text-amber-700"}`}>{s.product.barcode || "barcode will be created on print"}</div>
            </div>
            <div className="flex items-center rounded-lg border border-slate-200">
              <button onClick={() => setCopies(s.product.id, s.copies - 1)} className="grid h-9 w-8 place-items-center text-slate-500 hover:text-slate-900" data-testid={`tags-copies-dec-${s.product.id}`} aria-label="Fewer copies"><Minus className="h-3.5 w-3.5" /></button>
              <input value={s.copies} onChange={(e) => setCopies(s.product.id, parseInt(e.target.value, 10) || 1)} inputMode="numeric" data-testid={`tags-copies-input-${s.product.id}`} className="money h-9 w-11 border-x border-slate-200 text-center text-sm font-bold outline-none" aria-label="Copies" />
              <button onClick={() => setCopies(s.product.id, s.copies + 1)} className="grid h-9 w-8 place-items-center text-slate-500 hover:text-slate-900" data-testid={`tags-copies-inc-${s.product.id}`} aria-label="More copies"><Plus className="h-3.5 w-3.5" /></button>
            </div>
            <button onClick={() => remove(s.product.id)} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`tags-remove-${s.product.id}`} aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
          </div>
          {pharmacy && <BatchSelect s={s} onChange={(id) => setBatch(s.product.id, id)} />}
        </li>
      ))}
    </ul>
  );
}
